// What the lab promises on delivery, and what it costs.
//
//   Normal   — 7 days
//   Urgent   — 5 days
//   Express  — 2 days, and PKR 10,000 extra on the case
//
// This rides on the case's existing priority field rather than a new one: the three tiers line up
// exactly, and there is no case where a clinic would want a job marked express but delivered on the
// normal schedule. Priority IS the delivery promise.
//
// Everything about the promise lives here — the days, the surcharge, the wording shown to a doctor
// before they commit — so the wizard, the case editor and the invoice can never disagree about what
// was agreed.
import type { WfPriority } from './dto/workflow.js';

export interface TurnaroundTier {
  key: WfPriority;
  label: string;
  /** Calendar days from when the lab receives the records to when it ships. */
  days: number;
  /** Extra charge in PKR, on top of the per-aligner price. */
  surchargeRupees: number;
  /** The one-line promise, for a radio option. */
  summary: string;
}

/** What a clinic can choose today. */
export const TURNAROUNDS: TurnaroundTier[] = [
  { key: 'normal', label: 'Normal', days: 7, surchargeRupees: 0, summary: 'Ready in 7 days' },
  {
    key: 'emergency',
    label: 'Express',
    days: 2,
    surchargeRupees: 10_000,
    summary: 'Ready in 2 days · PKR 10,000 extra',
  },
];

/**
 * Every tier that can appear in the record, including Urgent, which the lab no longer offers.
 *
 * History is never rewritten: a case booked as Urgent last month was promised five days and must
 * still say five days. Reading it through this list keeps that true, while the shorter list above
 * is what anybody can actually pick — so no new case can be Urgent, and no old one silently
 * becomes Normal and gains two days it was never given.
 */
export const TURNAROUNDS_ALL: TurnaroundTier[] = [
  TURNAROUNDS[0]!,
  { key: 'urgent', label: 'Urgent (retired)', days: 5, surchargeRupees: 0, summary: 'Ready in 5 days' },
  TURNAROUNDS[1]!,
];

const BY_KEY = new Map(TURNAROUNDS_ALL.map((t) => [t.key, t]));

/** The tier for a priority, falling back to Normal for anything unrecognised. */
export const turnaroundOf = (priority: WfPriority | null | undefined): TurnaroundTier =>
  BY_KEY.get((priority ?? 'normal') as WfPriority) ?? TURNAROUNDS[0]!;

export const turnaroundDays = (priority: WfPriority | null | undefined): number => turnaroundOf(priority).days;

/** PKR extra for this tier. Only Express carries one. */
export const turnaroundSurchargeRupees = (priority: WfPriority | null | undefined): number =>
  turnaroundOf(priority).surchargeRupees;

/** Paisa, which is how money is stored everywhere in the app. */
export const turnaroundSurchargePaisa = (priority: WfPriority | null | undefined): number =>
  turnaroundSurchargeRupees(priority) * 100;

/** The line that appears on the invoice, so the clinic can see what the extra was for. */
export const turnaroundChargeLabel = (priority: WfPriority | null | undefined): string => {
  const t = turnaroundOf(priority);
  return `${t.label} delivery - ${t.days} day${t.days === 1 ? '' : 's'}`;
};

/**
 * The date the lab is committing to, counted from when it received the records.
 *
 * Deliberately calendar days, not working days: a clinic told "7 days" on a Thursday expects it the
 * following Thursday, and quietly turning that into nine days because of a weekend is how a lab
 * loses an argument it should never have had.
 */
export function turnaroundDueDate(receivedAt: string | Date, priority: WfPriority | null | undefined): string {
  const base = receivedAt instanceof Date ? receivedAt : new Date(receivedAt);
  const start = Number.isNaN(base.getTime()) ? new Date() : base;
  const due = new Date(start.getTime());
  due.setDate(due.getDate() + turnaroundDays(priority));
  return due.toISOString().slice(0, 10);
}

/**
 * True when this due date is exactly what the tier would have produced — i.e. nobody has overridden
 * it by hand.
 *
 * Changing the promise should move the date, but never over the top of a date a human deliberately
 * set: a lab that has agreed a specific day with a clinic must keep it.
 */
export function isDerivedDueDate(
  dueDate: string | null | undefined,
  receivedAt: string | null | undefined,
  priority: WfPriority | null | undefined,
): boolean {
  if (!dueDate || !receivedAt) return true; // nothing to protect
  return dueDate.slice(0, 10) === turnaroundDueDate(receivedAt, priority);
}
